import moment from "moment";

export const GENERAL_SERVICE_TYPES = [
  { value: "Paid (AW)", label: "Paid (AW)", serviceNo: 0, serviceType: "Paid (AW)", isScheduled: false },
  { value: "Accidental", label: "Accidental", serviceNo: 0, serviceType: "Accidental", isScheduled: false },
  { value: "Quick/Minor Repair", label: "Quick/Minor Repair", serviceNo: 0, serviceType: "Minor", isScheduled: false },
  { value: "Engine Overhaul", label: "Engine Overhaul", serviceNo: 0, serviceType: "Engine Overhaul", isScheduled: false },
  { value: "Repeat Job", label: "Repeat Job", serviceNo: 0, serviceType: "Repeat Job", isScheduled: false },
  { value: "AMC", label: "AMC", serviceNo: 0, serviceType: "AMC", isScheduled: false },
];

/**
 * Builds the complete dropdown list for service selection directly from the vehicle schedule.
 * Automatically filters out services already completed in past history.
 */
export function buildServiceOptions(services = [], jobOrders = [], currentJobCardId = null) {
  const options = [];

  let lastCompletedServiceNo = 0;
  if (Array.isArray(jobOrders)) {
    jobOrders.forEach((job) => {
      if (currentJobCardId && (job.id === currentJobCardId || job._id === currentJobCardId)) {
        return;
      }
      const sNo = parseInt(job.serviceNo, 10);
      if (sNo && sNo > lastCompletedServiceNo) {
        lastCompletedServiceNo = sNo;
      }
    });
  }

  if (Array.isArray(services) && services.length > 0) {
    const sorted = [...services].sort((a, b) => (parseInt(a.serviceNo, 10) || 0) - (parseInt(b.serviceNo, 10) || 0));

    sorted.forEach((s) => {
      const sNo = parseInt(s.serviceNo, 10);

      // Filter out services already completed in past history
      if (sNo <= lastCompletedServiceNo) {
        return;
      }

      const rawType = (s.serviceType || "FREE").toUpperCase();
      const formattedType = rawType === "FREE" ? "Free" : "Paid";

      options.push({
        value: `${sNo}-${formattedType}`,
        label: `${sNo} - ${formattedType}`,
        serviceNo: sNo,
        serviceType: formattedType,
        isScheduled: true,
        schedule: s,
      });

      // For FREE schedule milestones, also generate the corresponding PAID entry (e.g. 1 - Paid)
      if (rawType === "FREE") {
        options.push({
          value: `${sNo}-Paid`,
          label: `${sNo} - Paid`,
          serviceNo: sNo,
          serviceType: "Paid",
          isScheduled: true,
          schedule: s,
        });
      }
    });
  } else {
    // Default fallback if schedule is not yet loaded on vehicle
    const startNo = lastCompletedServiceNo + 1;
    for (let sNo = startNo; sNo <= startNo + 3; sNo++) {
      options.push({ value: `${sNo}-Free`, label: `${sNo} - Free`, serviceNo: sNo, serviceType: "Free", isScheduled: true });
      options.push({ value: `${sNo}-Paid`, label: `${sNo} - Paid`, serviceNo: sNo, serviceType: "Paid", isScheduled: true });
    }
  }

  // Append General non-scheduled services at the end
  GENERAL_SERVICE_TYPES.forEach((g) => options.push(g));

  return options;
}

/**
 * Determines the eligible service(s) based on vehicle schedule, completed jobs,
 * current KMS, and creation date (+5 grace days).
 * Supports dual eligibility (e.g. "1 - Paid or 2 - Free") when a Free service lapses.
 */
export function determineEligibleService({
  services = [],
  jobOrders = [],
  kms = 0,
  jobCardDate = moment(),
  currentJobCardId = null,
}) {
  const currentKm = parseInt(kms, 10) || 0;
  const targetDate = jobCardDate ? moment(jobCardDate).startOf("day") : moment().startOf("day");

  // Track completed service numbers from history
  let lastCompletedServiceNo = 0;
  if (Array.isArray(jobOrders)) {
    jobOrders.forEach((job) => {
      if (currentJobCardId && (job.id === currentJobCardId || job._id === currentJobCardId)) {
        return;
      }
      const sNo = parseInt(job.serviceNo, 10);
      if (sNo && sNo > lastCompletedServiceNo) {
        lastCompletedServiceNo = sNo;
      }
    });
  }

  const sortedServices = Array.isArray(services) && services.length > 0
    ? [...services].sort((a, b) => (parseInt(a.serviceNo, 10) || 0) - (parseInt(b.serviceNo, 10) || 0))
    : [];

  const eligibleCandidates = [];

  for (let i = 0; i < sortedServices.length; i++) {
    const s = sortedServices[i];
    const sNo = parseInt(s.serviceNo, 10) || i + 1;

    // Skip services already completed
    if (sNo <= lastCompletedServiceNo) {
      continue;
    }

    const rawType = (s.serviceType || "").toUpperCase();
    const isFree = rawType === "FREE";
    const maxKm = parseInt(s.serviceKms || s.serviceKm, 10) || 0;
    const dueDate = s.serviceDate ? moment(s.serviceDate).startOf("day") : null;
    const graceDueDate = dueDate ? moment(dueDate).add(5, "days").endOf("day") : null;

    if (isFree) {
      const isDateValid = !graceDueDate || targetDate.isSameOrBefore(graceDueDate);
      const isKmValid = currentKm <= maxKm;

      if (isKmValid && isDateValid) {
        // Qualified for this Free service milestone
        eligibleCandidates.push({
          value: `${sNo}-Free`,
          label: `${sNo} - Free`,
          serviceNo: sNo,
          serviceType: "Free",
        });
        break;
      } else {
        // Free service lapsed (KM exceeded or Date expired).
        // Check next milestone
        const nextService = sortedServices[i + 1];
        const nextMaxKm = nextService ? (parseInt(nextService.serviceKms || nextService.serviceKm, 10) || maxKm) : maxKm;
        const nextDueDate = nextService && nextService.serviceDate ? moment(nextService.serviceDate).startOf("day") : null;
        const nextGraceDueDate = nextDueDate ? moment(nextDueDate).add(5, "days").endOf("day") : null;
        const nextDateValid = !nextGraceDueDate || targetDate.isSameOrBefore(nextGraceDueDate);

        if (nextDateValid && (currentKm <= nextMaxKm || !nextService)) {
          // Add N - Paid as valid candidate
          eligibleCandidates.push({
            value: `${sNo}-Paid`,
            label: `${sNo} - Paid`,
            serviceNo: sNo,
            serviceType: "Paid",
          });

          // If next milestone (N+1) is Free and date/KM is valid, add (N+1) - Free as candidate
          if (nextService) {
            const nextNo = parseInt(nextService.serviceNo, 10) || sNo + 1;
            const nextRawType = (nextService.serviceType || "").toUpperCase();
            const nextIsFree = nextRawType === "FREE";

            if (nextIsFree && nextDateValid && currentKm <= nextMaxKm) {
              eligibleCandidates.push({
                value: `${nextNo}-Free`,
                label: `${nextNo} - Free`,
                serviceNo: nextNo,
                serviceType: "Free",
              });
            }
          }
          break;
        }
        // If next milestone is also date expired or KM exceeded, loop continues to next milestone
      }
    } else {
      // Paid periodic service milestone
      if (currentKm <= maxKm) {
        eligibleCandidates.push({
          value: `${sNo}-Paid`,
          label: `${sNo} - Paid`,
          serviceNo: sNo,
          serviceType: "Paid",
        });
        break;
      }
    }
  }

  // Safety fallback if no candidate matched
  if (eligibleCandidates.length === 0) {
    if (sortedServices.length > 0) {
      const last = sortedServices[sortedServices.length - 1];
      const maxKm = parseInt(last.serviceKms || last.serviceKm, 10) || 0;
      
      if (currentKm > maxKm) {
        eligibleCandidates.push({
          value: "Paid (AW)",
          label: "Paid (AW)",
          serviceNo: 0,
          serviceType: "Paid (AW)",
        });
      } else {
        const sNo = parseInt(last.serviceNo, 10);
        const rawType = (last.serviceType || "").toUpperCase();
        const formattedType = rawType === "FREE" ? "Free" : "Paid";
        eligibleCandidates.push({
          value: `${sNo}-${formattedType}`,
          label: `${sNo} - ${formattedType}`,
          serviceNo: sNo,
          serviceType: formattedType,
        });
      }
    } else {
      const sNo = lastCompletedServiceNo + 1;
      eligibleCandidates.push({
        value: `${sNo}-Free`,
        label: `${sNo} - Free`,
        serviceNo: sNo,
        serviceType: "Free",
      });
    }
  }

  const primary = eligibleCandidates[0];
  const eligibleLabel = eligibleCandidates.map((c) => c.label).join(" or ");

  return {
    eligible: primary,
    eligibleCandidates,
    eligibleLabel,
    lastCompletedServiceNo,
  };
}

/**
 * Gets the next scheduled service based on previous completed job orders.
 * Used for initial auto-fill before KMS is entered.
 */
export function getNextScheduledService(services = [], jobOrders = [], currentJobCardId = null) {
  let lastCompletedServiceNo = 0;
  if (Array.isArray(jobOrders)) {
    jobOrders.forEach((job) => {
      if (currentJobCardId && (job.id === currentJobCardId || job._id === currentJobCardId)) {
        return;
      }
      const sNo = parseInt(job.serviceNo, 10);
      if (sNo && sNo > lastCompletedServiceNo) {
        lastCompletedServiceNo = sNo;
      }
    });
  }

  const sortedServices = Array.isArray(services) && services.length > 0
    ? [...services].sort((a, b) => (parseInt(a.serviceNo, 10) || 0) - (parseInt(b.serviceNo, 10) || 0))
    : [];

  const next = sortedServices.find((s) => (parseInt(s.serviceNo, 10) || 0) > lastCompletedServiceNo);

  if (next) {
    const sNo = parseInt(next.serviceNo, 10);
    const rawType = (next.serviceType || "FREE").toUpperCase();
    const formattedType = rawType === "FREE" ? "Free" : "Paid";
    return {
      value: `${sNo}-${formattedType}`,
      label: `${sNo} - ${formattedType}`,
      serviceNo: sNo,
      serviceType: formattedType,
    };
  }

  if (sortedServices.length > 0) {
    const last = sortedServices[sortedServices.length - 1];
    const lastSNo = parseInt(last.serviceNo, 10);
    
    if (lastCompletedServiceNo >= lastSNo) {
      return {
        value: "Paid (AW)",
        label: "Paid (AW)",
        serviceNo: 0,
        serviceType: "Paid (AW)",
      };
    }
    
    const sNo = lastSNo;
    const rawType = (last.serviceType || "PAID").toUpperCase();
    const formattedType = rawType === "FREE" ? "Free" : "Paid";
    return {
      value: `${sNo}-${formattedType}`,
      label: `${sNo} - ${formattedType}`,
      serviceNo: sNo,
      serviceType: formattedType,
    };
  }

  const fallbackNo = lastCompletedServiceNo + 1;
  return {
    value: `${fallbackNo}-Free`,
    label: `${fallbackNo} - Free`,
    serviceNo: fallbackNo,
    serviceType: "Free",
  };
}
